import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Button, Chip, Stack } from "@mui/material";
import { PageContainer } from "@/features/ui";

import {
  EditSalesOrderForm,
  salesOrderStatusColors,
  salesOrderStatusLabels,
} from "@/features/sales-orders";
import { apiOutletClient, getErrorMessage } from "@/features/shared";
import {
  CenteredLoading,
  CustomTransactionHeader,
  useSnackbar,
} from "@/features/ui";

export const EditSalesOrder = () => {
  const { referenceNumber } = useParams();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();

  const [status, setStatus] = useState(null);
  const [creatorName, setCreatorName] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [defaultValues, setDefaultValues] = useState(null);

  useEffect(() => {
    const fetchSalesOrder = async () => {
      setIsLoading(true);
      try {
        const {
          data: { data },
        } = await apiOutletClient.get(`sales-orders/${referenceNumber}`, {
          params: {
            withBuyer: true,
            withSeller: true,
            withWarehouse: true,
            withProductVariants: true,
            withPaymentTerm: true,
            withCreator: true,
          },
        });

        setStatus(data.status);
        setCreatorName(data.creator?.name);

        setDefaultValues({
          taxable: data.taxable,
          totalIncludesTax: data.totalIncludesTax,
          taxPercentage: data.taxPercentage,
          taxAmount: data.taxAmount,
          transactionDate: dayjs(data.transactionDate),
          warehouse: data.warehouse,
          buyer: data.buyer,
          seller: data.seller,
          paymentTerm: data.paymentTerm,
          note: data.note,
          discount: data.discount || 0,
          discountInput: data.discountAmount || 0,
          discountType: data.discountType,
          grandTotal: data.grandTotal,
          subtotal: data.subtotal,
          salesOrderProductVariants:
            data.salesOrderGeneralProductVariants?.map((variant) => {
              // Calculate maxQuantity if linked to sales request
              const maxQuantity = variant.salesRequestProductVariant
                ? variant.quantity +
                  (variant.salesRequestProductVariant
                    .purchaseOrderProductVariant.quantity -
                    variant.salesRequestProductVariant.orderedQuantity)
                : null; // Manual entries have no max limit

              const minQuantity = Math.max(
                variant.deliveredQuantity || 0,
                variant.invoicedQuantity || 0,
              );

              return {
                id: variant.id,
                productVariantId: variant.productVariantId,
                name: variant.name,
                sku: variant.sku,
                unitOfMeasurement: variant.product?.unitOfMeasurement?.name,
                quantity: variant.quantity,
                discount: variant.discount || 0,
                costPerUnit: variant.price,
                note: variant.note,
                deliveredQuantity: variant.deliveredQuantity,
                invoicedQuantity: variant.invoicedQuantity,
                maxQuantity,
                minQuantity,
                salesRequestProductVariant: variant.salesRequestProductVariant,
              };
            }) || [],
          salesOrderLensProducts:
            data.salesOrderLensProducts?.map((lensProduct) => {
              // Get sales request info from first variant
              const firstVariant =
                lensProduct.salesOrderLensProductVariants?.[0];
              const salesRequest =
                firstVariant?.salesRequestProductVariant?.salesRequest;
              const purchaseOrderId = salesRequest?.purchaseOrderId;

              // Transform to match LensProductItem expected structure
              return {
                id: lensProduct.id,
                name: lensProduct.name,
                availability: lensProduct.availability,
                discount: lensProduct.discount || 0,
                note: lensProduct.note,
                price: lensProduct.price,
                // Structure expected by LensProductItem component
                salesRequest: {
                  referenceNumber: salesRequest?.referenceNumber || "",
                  purchaseOrder: {
                    id: purchaseOrderId,
                    note: lensProduct.note,
                  },
                },
                // Transform lens product variants to match expected structure
                salesRequestProductVariants:
                  lensProduct.salesOrderLensProductVariants.map((lpv) => {
                    return {
                      id: lpv.salesRequestProductVariantId,
                      purchaseOrderProductVariant: {
                        id: lpv.salesRequestProductVariant
                          ?.purchaseOrderProductVariantId,
                        productId: lpv.productId,
                        side: lpv.side,
                        sku: lpv.sku,
                        note: lpv.note,
                        variantAttribute1Value: lpv.variantAttribute1Value,
                        variantAttribute2Value: lpv.variantAttribute2Value,
                        variantAttribute3Value: lpv.variantAttribute3Value,
                        product: {
                          name: lensProduct.name, // Use lens product name
                        },
                      },
                    };
                  }),
              };
            }) || [],
        });
      } catch (error) {
        const errorMessage =
          getErrorMessage(error, "Gagal memperbarui pesanan penjualan");
        showSnackbar(errorMessage, "error");
      } finally {
        setIsLoading(false);
      }
    };
    fetchSalesOrder();
  }, [referenceNumber, showSnackbar]);

  const handleSubmit = async (formPayload) => {
    try {
      await apiOutletClient.patch(
        `sales-orders/${referenceNumber}`,
        formPayload,
        { headers: { "Outlet-Id": defaultValues.seller.id } },
      );
      showSnackbar("Pesanan penjualan berhasil diperbarui", "success");
      navigate(-1);
    } catch (error) {
      const errorMessage =
        getErrorMessage(error, "Gagal memperbarui pesanan penjualan");
      showSnackbar(errorMessage, "error");
    }
  };

  if (!defaultValues && isLoading) {
    return <CenteredLoading />;
  }

  return (
    <PageContainer maxWidth="xl">
      <Stack spacing={2}>
        <CustomTransactionHeader
          title="Ubah Pesanan Penjualan"
          referenceNumber={referenceNumber}
          creatorName={creatorName}
          statusChip={
            status && (
              <Chip
                label={salesOrderStatusLabels[status]}
                color={salesOrderStatusColors[status]}
              />
            )
          }
        />
        <EditSalesOrderForm
          defaultValues={defaultValues}
          isLoading={isLoading}
          onSubmit={handleSubmit}
          renderActionButton={({ isSubmitting }) => (
            <Button type="submit" variant="contained" loading={isSubmitting}>
              Simpan
            </Button>
          )}
        />
      </Stack>
    </PageContainer>
  );
};
